class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }
  connectedCallback(){
    if(window.location.hash === '#cart-open'){
      this.open();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.thresholdsMet = new Set();
  }

  connectedCallback() {
    this.checkThresholds();
  }

  async checkThresholds() {
    this.setCheckoutButtonDisabled(true);

    const total = parseFloat(this.dataset.total) || 0;
    const minSpend1 = parseFloat(this.dataset.minSpend_1) || 0;
    const minSpend2 = parseFloat(this.dataset.minSpend_2) || 0;
    const minSpend3 = parseFloat(this.dataset.minSpend_3) || 0;
    let currentThreshold = 0;
    if (total >= minSpend3) currentThreshold = 3;
    else if (total >= minSpend2) currentThreshold = 2;
    else if (total >= minSpend1) currentThreshold = 1;
    else currentThreshold = 0;
    if (currentThreshold >= 1 && !this.thresholdsMet.has('threshold1')) {
      await this.addGiftProducts('giftsTier_1');
      this.thresholdsMet.add('threshold1');
    }
    if (currentThreshold >= 2 && !this.thresholdsMet.has('threshold2')) {
      await this.addGiftProducts('giftsTier_2');
      this.thresholdsMet.add('threshold2');
    }
    if (currentThreshold >= 3 && !this.thresholdsMet.has('threshold3')) {
      await this.addGiftProducts('giftsTier_3');
      this.thresholdsMet.add('threshold3');
    }
    if (currentThreshold < 3) {
      const upsellsToRemove = this.dataset.giftsTier_3;
      if (upsellsToRemove && upsellsToRemove.trim() !== '') {
        await this.removeUpsellProducts(upsellsToRemove);
      }
    }
    if (currentThreshold < 2) {
      const upsellsToRemove = this.dataset.giftsTier_2;
      if (upsellsToRemove && upsellsToRemove.trim() !== '') {
        await this.removeUpsellProducts(upsellsToRemove);
      }
    }
    if (currentThreshold < 1) {
      let upsellsToRemove = this.dataset.giftsTier_1;
      const giftItem = document.querySelector('[data-gift-item="true"]');
      if (giftItem) {
        const giftVariantId = giftItem.getAttribute('data-variant-id');
        if (giftVariantId) {
          if (upsellsToRemove && upsellsToRemove.trim() !== '') {
            upsellsToRemove += ',' + giftVariantId;
          } else {
            upsellsToRemove = giftVariantId;
          }
        }
      }
      if (upsellsToRemove && upsellsToRemove.trim() !== '') {
        await this.removeUpsellProducts(upsellsToRemove);
      }
    }

    await this.enforceGiftQuantityLimit();
    this.setCheckoutButtonDisabled(false);
  }

  async removeUpsellProducts(upsellsToRemove) {
    if (!upsellsToRemove || upsellsToRemove.trim() === '') {
      return;
    }
    const productIds = upsellsToRemove.split(',').map(id => id.trim()).filter(id => id);
    if (productIds.length === 0) {
      return;
    }
    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      const itemsToRemove = cartData.items.filter(item => {
        if (item.variant_id && productIds.includes(item.variant_id.toString())) {
          return true;
        }
        if (item.id && productIds.includes(item.id.toString())) {
          return true;
        }
        if (item.product_id && productIds.includes(item.product_id.toString())) {
          return true;
        }
        return false;
      });
      if (itemsToRemove.length === 0) {
        return;
      }
      const updates = {};
      itemsToRemove.forEach(item => {
        updates[item.key] = 0;
      });
      const body = JSON.stringify({ updates });
      const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      if (response.ok) {
        const cart = await response.json();
        await this.updateCartDrawer();
      } else {
        const errorText = await response.text();
        console.error('Error removing gift products:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error removing gift products:', error);
    }
  }

  async addGiftProducts(giftKey) {
    const giftIds = this.dataset[giftKey];
    if (!giftIds || giftIds.trim() === '') {
      return;
    }
    const productIds = giftIds.split(',').map(id => id.trim()).filter(id => id);
    if (productIds.length === 0) {
      return;
    }
    try {
      const cartItems = document.querySelectorAll('[data-variant-id]');
      const existingProductIds = Array.from(cartItems).map(item => item.getAttribute('data-variant-id'));
      const productsToAdd = productIds.filter(productId => !existingProductIds.includes(productId));
      if (productsToAdd.length === 0) {
        return;
      }
      for (const productId of productsToAdd) {
        await this.addToCart(productId, 1);
      }
      await this.updateCartDrawer();
    } catch (error) {
      console.error(`Error adding gift products for ${giftKey}:`, error);
    }
  }

  async enforceGiftQuantityLimit() {
    try {
      const tiers = [
        this.dataset.giftsTier_1 || '',
        this.dataset.giftsTier_2 || '',
        this.dataset.giftsTier_3 || ''
      ]
        .filter(Boolean)
        .join(',');

      if (!tiers || tiers.trim() === '') {
        return;
      }

      let giftVariantIds = tiers
        .split(',')
        .map(id => id.trim())
        .filter(id => id);

      const domGiftVariantIds = Array.from(document.querySelectorAll('[data-gift-item="true"][data-variant-id]'))
        .map(node => node.getAttribute('data-variant-id'))
        .filter(id => id);
      if (domGiftVariantIds.length > 0) {
        giftVariantIds = Array.from(new Set([...giftVariantIds, ...domGiftVariantIds]));
      }

      if (giftVariantIds.length === 0) {
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      const updates = {};
      for (const item of cartData.items) {
        const variantIdStr = item.variant_id ? item.variant_id.toString() : '';
        if (variantIdStr && giftVariantIds.includes(variantIdStr) && item.quantity > 1) {
          updates[item.key] = 1;
        }
      }

      if (Object.keys(updates).length === 0) {
        return;
      }

      const body = JSON.stringify({ updates });
      const cartUpdateUrl = window.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(cartUpdateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      if (response.ok) {
        await response.json();
        await this.updateCartDrawer();
      } else {
        const errorText = await response.text();
        console.error('Error enforcing gift quantity limit:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error enforcing gift quantity limit:', error);
    }
  }

  async addToCart(productId, quantity) {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(productId, 10),
          quantity: quantity
        }]
      })
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.description || 'Error adding item to cart');
    }
    return response.json();
  }

  async updateCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart?sections=cart-drawer,cart-icon-bubble`
      );
      const sectionsData = await response.json();
      cartDrawer.renderContents({ sections: sectionsData });
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  setCheckoutButtonDisabled(disabled) {
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    const giftInCart = checkoutButton?.getAttribute('data-gift-in-cart');
    if (checkoutButton && giftInCart !== 'false') {
      checkoutButton.disabled = disabled;
      if (disabled) {
        checkoutButton.setAttribute('aria-disabled', 'true');
        checkoutButton.classList.add('disabled');
      } else {
        checkoutButton.removeAttribute('aria-disabled');
        checkoutButton.classList.remove('disabled');
      }
    }
  }
}

customElements.define('progress-bar', ProgressBar);
class GiftSelect extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (evt) => {
      evt.target.closest('.gift-select-wrapper').querySelector('upsell-add-to-cart-button').setAttribute('data-product-id', evt.target.querySelector('option:checked').value);
    });
  }
}

customElements.define('gift-select', GiftSelect);